import { format, zonedTimeToUtc, utcToZonedTime, formatInTimeZone } from "date-fns-tz";
import { TaskStartDatePeriod } from "Models/task";

export interface DateTimeInput {
  date: string; // YYYY-MM-DD format
  time?: string; // HH:MM format
}

export class TaskDateUtils {
  /**
   * Combine date and time inputs to create a UTC ISO string
   * @param dateInput - Date in YYYY-MM-DD format
   * @param timeInput - Time in HH:MM format (optional)
   * @param preserveExistingTime - If true and timeInput is empty, preserve time from existing date
   * @param existingDateString - Existing date string to preserve time from
   */
  static createTaskDateTime(
    data: {
      dateInput: string;
      timeInput?: string;
      preserveExistingTime?: boolean;
      existingDateString?: string;
    },
    timezone?: string
  ): string {
    const { dateInput, timeInput, preserveExistingTime = false, existingDateString } = data;
    if (!dateInput) return "";

    // Parse the date components
    const [year, month, day] = dateInput.split("-").map(Number);

    let hours = 0;
    let minutes = 0;

    if (timeInput) {
      // Use provided time
      const [h, m] = timeInput.split(":").map(Number);
      hours = h;
      minutes = m;
    } else if (preserveExistingTime && existingDateString && this.isValidDate(existingDateString)) {
      // Preserve time from existing date
      const existingDate = this.extractTimeForInput(existingDateString, { timezone });
      hours = Number(existingDate.split(":")[0]);
      minutes = Number(existingDate.split(":")[1]);
    }

    // Create date in user's local timezone
    // const localDate = new Date(year, month - 1, day, hours, minutes);

    const pad = (num: number) => num.toString().padStart(2, "0");

    const localDate = `${year}-${pad(month)}-${pad(day)}T${pad(hours)}:${pad(minutes)}:00`;

    // Convert to UTC for storage
    try {
      const utcDate = zonedTimeToUtc(localDate, timezone!);
      if (Number.isNaN(utcDate.getTime())) return "";
      return utcDate.toISOString();
    } catch {
      return "";
    }
  }

  /**
   * Create start and end dates for predefined periods
   */
  static createPeriodDates(
    dateInput: string,
    period: TaskStartDatePeriod,
    existingStartDate?: string,
    timezone?: string
  ): { startDate: string; endDate: string } {
    if (!dateInput) return { startDate: "", endDate: "" };

    const [year, month, day] = dateInput.split("-").map(Number);

    // Default time periods (you can adjust these based on your requirements)
    const PERIODS: Partial<
      Record<
        TaskStartDatePeriod,
        { start: { h: number; m: number }; end: { h: number; m: number } }
      >
    > = {
      [TaskStartDatePeriod.MORNING]: { start: { h: 6, m: 0 }, end: { h: 12, m: 0 } },
      [TaskStartDatePeriod.AFTERNOON]: { start: { h: 12, m: 0 }, end: { h: 18, m: 0 } },
      [TaskStartDatePeriod.EVENING]: { start: { h: 18, m: 0 }, end: { h: 23, m: 59 } },
      [TaskStartDatePeriod.FULL_DAY]: { start: { h: 6, m: 0 }, end: { h: 18, m: 0 } },
    };

    const periodConfig = PERIODS[period];
    if (!periodConfig) {
      // For custom or other periods, preserve existing times or use defaults
      const baseDate = existingStartDate
        ? new Date(existingStartDate)
        : new Date(year, month - 1, day, 9, 0);
      const startDate = this.createTaskDateTime(
        { dateInput, preserveExistingTime: true, existingDateString: existingStartDate },
        timezone
      );
      const endLocalDate = new Date(baseDate.getTime() + 60 * 60 * 1000); // Add 1 hour
      const endDate = zonedTimeToUtc(endLocalDate, timezone!).toISOString();

      return { startDate, endDate };
    }

    const startTime = `${String(periodConfig.start.h).padStart(2, "0")}:${String(
      periodConfig.start.m
    ).padStart(2, "0")}`;

    const startDate = this.createTaskDateTime({ dateInput, timeInput: startTime }, timezone);

    const endTime = `${String(periodConfig.end.h).padStart(2, "0")}:${String(
      periodConfig.end.m
    ).padStart(2, "0")}`;
    const endDate = this.createTaskDateTime(
      {
        dateInput,
        timeInput: endTime,
      },
      timezone
    );

    return { startDate, endDate };
  }

  /**
   * Create multi-day task dates
   */
  static createMultiDayDates(
    startDateInput: string,
    endDateInput: string,
    startTimeInput?: string,
    endTimeInput?: string,
    preserveExistingTimes = false,
    existingStartDate?: string,
    existingEndDate?: string,
    timezone?: string
  ): { startDate: string; endDate: string } {
    const startDate = this.createTaskDateTime(
      {
        dateInput: startDateInput,
        timeInput: startTimeInput,
        preserveExistingTime: preserveExistingTimes,
        existingDateString: existingStartDate,
      },
      timezone
    );

    const endDate = this.createTaskDateTime(
      {
        dateInput: endDateInput,
        timeInput: endTimeInput,
        preserveExistingTime: preserveExistingTimes,
        existingDateString: existingEndDate,
      },
      timezone
    );

    return { startDate, endDate };
  }

  /**
   * Extract date part for input fields (always in local timezone)
   */
  static extractDateForInput(
    dateString: string,
    options: {
      timezone?: string;
      formatFunction?: (date: Date, formatString: string) => string;
    } = {}
  ): string {
    if (!dateString || !this.isValidDate(dateString)) return "";
    const { formatFunction, timezone = Intl.DateTimeFormat().resolvedOptions().timeZone } = options;

    try {
      if (!formatFunction) {
        // return format(utcToZonedTime(dateString, timezone), "yyyy-MM-dd");
        return formatInTimeZone(dateString, timezone, "yyyy-MM-dd");
      }

      // Convert UTC date to local date for display
      return formatFunction(new Date(dateString), "yyyy-MM-dd");
    } catch {
      return "";
    }
  }

  /**
   * Extract time part for input fields (always in local timezone)
   */
  static extractTimeForInput(
    dateString: string,
    options: {
      timezone?: string;
      formatFunction?: (date: Date, formatString: string) => string;
    } = {}
  ): string {
    if (!dateString || !this.isValidDate(dateString)) return "";
    const { formatFunction, timezone = Intl.DateTimeFormat().resolvedOptions().timeZone } = options;

    try {
      if (!formatFunction) {
        // return format(utcToZonedTime(dateString, timezone), "HH:mm");
        return formatInTimeZone(dateString, timezone, "HH:mm");
      }

      // Convert UTC date to local time for display
      return formatFunction(new Date(dateString), "HH:mm");
    } catch {
      return "";
    }
  }

  /**
   * Format date for display (always in local timezone)
   */
  static formatForDisplay(
    dateString: string,
    formatPattern = "dd/MM/yyyy",
    options: {
      timezone?: string;
      formatFunction?: (date: Date | string, formatString: string) => string;
    } = {}
  ): string {
    if (!dateString || !this.isValidDate(dateString)) return "";
    const { formatFunction, timezone = Intl.DateTimeFormat().resolvedOptions().timeZone } = options;

    try {
      if (!formatFunction) {
        return formatInTimeZone(dateString, timezone, formatPattern);
      }

      return formatFunction(new Date(dateString), formatPattern);
    } catch {
      return "";
    }
  }

  /**
   * Format date and time for display (always in local timezone)
   */
  static formatDateTimeForDisplay(
    dateString: string,
    formatPattern = "dd/MM/yyyy HH:mm",
    options: {
      timezone?: string;
      formatFunction?: (date: Date, formatString: string) => string;
    } = {}
  ): string {
    if (!dateString || !this.isValidDate(dateString)) return "";
    const { formatFunction, timezone = Intl.DateTimeFormat().resolvedOptions().timeZone } = options;

    try {
      if (!formatFunction) {
        return formatInTimeZone(dateString, timezone, formatPattern);
      }

      return formatFunction(new Date(dateString), formatPattern);
    } catch {
      return "";
    }
  }

  /**
   * Check if a date is valid
   */
  static isValidDate(dateString?: string | null): boolean {
    if (!dateString) return false;
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  }

  /**
   * Validate that end date is after start date
   */
  static isEndDateValid(startDate: string, endDate: string): boolean {
    if (!startDate || !endDate) return true; // Allow empty dates

    const start = new Date(startDate);
    const end = new Date(endDate);

    return end > start;
  }

  /**
   * Create time slots for variable time tasks
   */
  static createTimeSlotDateTime(
    dateInput: string,
    timeFrom: string,
    timeTo: string,
    timezone?: string
  ): {
    date: string;
    from: string;
    to: string;
    startDateTime: string;
    endDateTime: string;
  } {
    const startDateTime = this.createTaskDateTime({ dateInput, timeInput: timeFrom }, timezone);
    const endDateTime = this.createTaskDateTime({ dateInput, timeInput: timeTo }, timezone);

    return {
      date: dateInput,
      from: timeFrom,
      to: timeTo,
      startDateTime,
      endDateTime,
    };
  }

  /**
   * Get minimum date for date inputs (e.g., 19 days ago)
   */
  static getMinimumDate(
    daysBack = 19,
    options: { formatFunction?: (date: Date, formatString: string) => string } = {}
  ): string {
    const { formatFunction = format } = options;
    const minDate = new Date();
    minDate.setDate(minDate.getDate() - daysBack);
    return formatFunction(minDate, "yyyy-MM-dd");
  }

  /**
   * Get today's date for default values
   */
  static getTodayForInput(
    currentDate?: Date,
    options: {
      formatFunction?: (date: Date | string, formatString: string) => string;
      timezone?: string;
    } = {}
  ): string {
    const { formatFunction, timezone = Intl.DateTimeFormat().resolvedOptions().timeZone } = options;
    const current = currentDate || new Date().toISOString();

    if (!formatFunction) {
      return formatInTimeZone(current, timezone, "yyyy-MM-dd");
    }

    return formatFunction(current, "yyyy-MM-dd");
  }

  /**
   * Add buffer time to end date based on period
   */
  static addBufferTime(startDate: string, period: TaskStartDatePeriod): string {
    if (!startDate) return "";

    const start = new Date(startDate);
    let bufferHours = 1; // default buffer

    switch (period) {
      case TaskStartDatePeriod.MULTI_DAY:
        bufferHours = 24; // 1 day
        break;
      case TaskStartDatePeriod.FULL_DAY:
        bufferHours = 8; // 8 hours
        break;
      case TaskStartDatePeriod.MORNING:
      case TaskStartDatePeriod.AFTERNOON:
      case TaskStartDatePeriod.EVENING:
        bufferHours = 4; // 4 hours
        break;
      default:
        bufferHours = 1; // 1 hour
    }

    const endDate = new Date(start.getTime() + bufferHours * 60 * 60 * 1000);

    return endDate.toISOString();
  }
}

export default TaskDateUtils;
